// SPDX-License-Identifier: AGPL-3.0-or-later

export interface ExistingAvailabilityEvent {
	start: string | null;
	end: string | null;
	attendees: ReadonlyArray<{address: string}>;
}

export interface AvailabilityRequest {
	emails: Array<string>;
	start: string;
	end: string;
}

/**
 * Free/busy does not identify events. For existing invitees, check only time
 * added outside the original reservation rather than counting it as a clash.
 * New invitees still need the entire proposed window checked.
 */
export function availabilityRequests(
	emails: ReadonlyArray<string>,
	start: string,
	end: string,
	existing?: ExistingAvailabilityEvent | null,
): Array<AvailabilityRequest> {
	const from = new Date(start).getTime();
	const until = new Date(end).getTime();
	if (!Number.isFinite(from) || !Number.isFinite(until) || until <= from || emails.length === 0) return [];
	const request = (addresses: Array<string>, first: number, last: number): AvailabilityRequest => ({
		emails: addresses,
		start: new Date(first).toISOString(),
		end: new Date(last).toISOString(),
	});
	const oldStart = existing?.start ? new Date(existing.start).getTime() : NaN;
	const oldEnd = existing?.end ? new Date(existing.end).getTime() : NaN;
	if (!Number.isFinite(oldStart) || !Number.isFinite(oldEnd) || oldEnd <= oldStart) {
		return [request([...emails], from, until)];
	}
	const oldEmails = new Set(existing?.attendees.map((entry) => entry.address.toLowerCase()));
	const added = emails.filter((email) => !oldEmails.has(email.toLowerCase()));
	const retained = emails.filter((email) => oldEmails.has(email.toLowerCase()));
	const requests = added.length > 0 ? [request(added, from, until)] : [];
	if (retained.length > 0) {
		if (from < oldStart) requests.push(request(retained, from, Math.min(until, oldStart)));
		if (until > oldEnd) requests.push(request(retained, Math.max(from, oldEnd), until));
	}
	return requests;
}
